import type { TenantLinkKeyT } from "@/app/config/tenant-link-types";
import navigationSsot from "../../config/navigation-ssot.cjs";

const {
  BASE_FOOTER_MENU_SECTION_DEFINITIONS: rawBaseFooterMenuSectionDefinitions,
  BASE_MENU_ITEM_DEFINITIONS: rawBaseMenuItemDefinitions,
  GENERAL_EXTERNAL_REDIRECTS: rawGeneralExternalRedirects,
  LEGAL_LINK_DEFINITIONS: rawLegalLinkDefinitions,
  ROUTE_PATHS_BY_KEY: rawRoutePathsByKey,
  TENANT_EXTERNAL_REDIRECTS_BY_KEY: rawTenantExternalRedirectsByKey,
  TENANT_FOOTER_MENU_SECTION_DEFINITIONS_BY_KEY:
    rawTenantFooterMenuSectionDefinitionsByKey,
  TENANT_LEGAL_LINK_DEFINITIONS_BY_KEY: rawTenantLegalLinkDefinitionsByKey,
  TENANT_SECTION_TEMPLATE_DEFINITIONS: rawTenantSectionTemplateDefinitions,
} = navigationSsot;

export type NavigationRouteKeyT =
  | "home"
  | "vehicles"
  | "offers"
  | "vehicle-search"
  | "models"
  | "vehicle-purchase"
  | "service"
  | "online-appointments"
  | "workshop-appointment"
  | "financial-services"
  | "audi-service"
  | "camper"
  | "camper-service"
  | "mobility"
  | "electric-mobility"
  | "fleet"
  | "fleet-services"
  | "fleet-management"
  | "fleet-utilities"
  | "wiestcars"
  | "about"
  | "contacts"
  | "blog"
  | "careers"
  | "history"
  | "contact"
  | "imprint"
  | "privacy"
  | "accessibility"
  | "eu-garan"
  | "gender"
  | "partner";

type NavigationLinkDefinitionT = {
  href?: string;
  routeKey?: NavigationRouteKeyT;
  external?: boolean;
  tenantLinkKey?: TenantLinkKeyT;
};

export type NavigationMenuItemT = {
  title: string;
  href: string;
  children?: NavigationMenuItemT[];
  tenantLinkKey?: TenantLinkKeyT;
  separatorBefore?: boolean;
  trackedPaths?: string[];
  external?: boolean;
};

export type NavigationFooterMenuLinkT = {
  href: string;
  title: string;
  external?: boolean;
  tenantLinkKey?: TenantLinkKeyT;
};

export type NavigationFooterMenuSectionT = {
  id: string;
  title: string;
  links: NavigationFooterMenuLinkT[];
};

type NavigationMenuItemDefinitionT = NavigationLinkDefinitionT & {
  title: string;
  children?: NavigationMenuItemDefinitionT[];
  separatorBefore?: boolean;
  trackedPaths?: string[];
};

type NavigationFooterMenuLinkDefinitionT = NavigationLinkDefinitionT & {
  title: string;
};

type NavigationFooterMenuSectionDefinitionT = {
  id: string;
  title: string;
  links: NavigationFooterMenuLinkDefinitionT[];
};

export type NavigationLegalLinkT = {
  href: string;
  title: string;
};

export type TenantSectionTemplateT = {
  submenuTemplate: NavigationMenuItemT[];
};

type TenantSectionTemplateDefinitionT = {
  submenuTemplate: NavigationMenuItemDefinitionT[];
};

type ExternalTenantKeyT = "autohaus-rauch" | "schuetz-gross-umstadt";

const ROUTE_PATHS_BY_KEY = rawRoutePathsByKey as Record<
  NavigationRouteKeyT,
  string
>;

const BASE_MENU_ITEM_DEFINITIONS =
  rawBaseMenuItemDefinitions as NavigationMenuItemDefinitionT[];

const BASE_FOOTER_MENU_SECTION_DEFINITIONS =
  rawBaseFooterMenuSectionDefinitions as NavigationFooterMenuSectionDefinitionT[];

const LEGAL_LINK_DEFINITIONS =
  rawLegalLinkDefinitions as NavigationFooterMenuLinkDefinitionT[];

const TENANT_FOOTER_MENU_SECTION_DEFINITIONS_BY_KEY =
  rawTenantFooterMenuSectionDefinitionsByKey as Partial<
    Record<ExternalTenantKeyT, NavigationFooterMenuSectionDefinitionT[]>
  >;

const TENANT_LEGAL_LINK_DEFINITIONS_BY_KEY =
  rawTenantLegalLinkDefinitionsByKey as Partial<
    Record<ExternalTenantKeyT, NavigationFooterMenuLinkDefinitionT[]>
  >;

const TENANT_SECTION_TEMPLATE_DEFINITIONS =
  rawTenantSectionTemplateDefinitions as Record<
    ExternalTenantKeyT,
    TenantSectionTemplateDefinitionT
  >;

const TENANT_EXTERNAL_REDIRECTS_BY_KEY =
  rawTenantExternalRedirectsByKey as Record<string, Record<string, string>>;

const GENERAL_EXTERNAL_REDIRECTS = rawGeneralExternalRedirects as Record<
  string,
  string
>;

const resolveNavigationHref = (link: NavigationLinkDefinitionT): string => {
  if (link.routeKey) {
    return ROUTE_PATHS_BY_KEY[link.routeKey];
  }

  return link.href ?? ROUTE_PATHS_BY_KEY.home;
};

const resolveMenuItemDefinition = (
  item: NavigationMenuItemDefinitionT
): NavigationMenuItemT => ({
  title: item.title,
  href: resolveNavigationHref(item),
  tenantLinkKey: item.tenantLinkKey,
  separatorBefore: item.separatorBefore,
  trackedPaths: item.trackedPaths,
  external: item.external,
  ...(item.children
    ? {
        children: item.children.map(resolveMenuItemDefinition),
      }
    : null),
});

const resolveFooterLinkDefinition = (
  link: NavigationFooterMenuLinkDefinitionT
): NavigationFooterMenuLinkT => ({
  href: resolveNavigationHref(link),
  title: link.title,
  external: link.external,
  tenantLinkKey: link.tenantLinkKey,
});

export const getRoutePath = (routeKey: NavigationRouteKeyT): string =>
  ROUTE_PATHS_BY_KEY[routeKey];

export const getBaseMenuItems = (): NavigationMenuItemT[] =>
  BASE_MENU_ITEM_DEFINITIONS.map(resolveMenuItemDefinition);

export const getBaseFooterMenuSections = (): NavigationFooterMenuSectionT[] =>
  BASE_FOOTER_MENU_SECTION_DEFINITIONS.map((section) => ({
    id: section.id,
    title: section.title,
    links: section.links.map(resolveFooterLinkDefinition),
  }));

export const getTenantFooterMenuSections = (
  tenantKey: ExternalTenantKeyT
): NavigationFooterMenuSectionT[] | null => {
  const sectionDefinitions =
    TENANT_FOOTER_MENU_SECTION_DEFINITIONS_BY_KEY[tenantKey];
  if (!sectionDefinitions) {
    return null;
  }

  return sectionDefinitions.map((section) => ({
    id: section.id,
    title: section.title,
    links: section.links.map(resolveFooterLinkDefinition),
  }));
};

export const getLegalLinks = (): NavigationLegalLinkT[] =>
  LEGAL_LINK_DEFINITIONS.map((link) => ({
    href: resolveNavigationHref(link),
    title: link.title,
  }));

export const getTenantLegalLinks = (
  tenantKey: ExternalTenantKeyT
): NavigationLegalLinkT[] | null => {
  const legalDefinitions = TENANT_LEGAL_LINK_DEFINITIONS_BY_KEY[tenantKey];
  if (!legalDefinitions) {
    return null;
  }

  return legalDefinitions.map((link) => ({
    href: resolveNavigationHref(link),
    title: link.title,
  }));
};

export const getGeneralExternalRedirects = (): Record<string, string> => ({
  ...GENERAL_EXTERNAL_REDIRECTS,
});

export const getTenantExternalRedirects = (
  tenantKey: string
): Record<string, string> => ({
  ...(TENANT_EXTERNAL_REDIRECTS_BY_KEY[tenantKey] ?? {}),
});

export const getTenantSectionTemplate = (
  tenantKey: ExternalTenantKeyT
): TenantSectionTemplateT => ({
  submenuTemplate: TENANT_SECTION_TEMPLATE_DEFINITIONS[
    tenantKey
  ].submenuTemplate.map(resolveMenuItemDefinition),
});
